import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { OnboardingProfile } from "@/hooks/useOnboardingProfile";

export type OnboardingStep = 1 | 2 | 3 | 4;

export interface ProfessionalFormData {
  name: string;
  email: string;
  password: string;
  healthAreaId: string;
}

interface OnboardingContextValue {
  step: OnboardingStep | null;
  setStep: (step: OnboardingStep) => void;
  isAuthenticated: boolean;
  setIsAuthenticated: (v: boolean) => void;
  professionalForm: ProfessionalFormData;
  setProfessionalForm: React.Dispatch<React.SetStateAction<ProfessionalFormData>>;
  sessionId: string | null;
  setSessionId: (id: string | null) => void;
  patient: { id: string; name: string; cpf?: string } | null;
  setPatient: React.Dispatch<
    React.SetStateAction<{ id: string; name: string; cpf?: string } | null>
  >;
  resolveStepFromProfile: (
    profile: OnboardingProfile | null,
    hasSession: boolean
  ) => OnboardingStep;
}

const defaultForm: ProfessionalFormData = {
  name: "",
  email: "",
  password: "",
  healthAreaId: "",
};

const OnboardingContext = createContext<OnboardingContextValue | null>(null);

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const [step, setStepState] = useState<OnboardingStep | null>(1);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [professionalForm, setProfessionalForm] =
    useState<ProfessionalFormData>(defaultForm);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [patient, setPatient] = useState<{
    id: string;
    name: string;
    cpf?: string;
  } | null>(null);

  const resolveStepFromProfile = useCallback(
    (
      profile: OnboardingProfile | null,
      hasSession: boolean
    ): OnboardingStep => {
      if (!hasSession) return 1;
      if (!profile) return 1;
      if (!profile.email_confirmed) return 2;
      if (!profile.health_area_id) return 1;
      return 3;
    },
    []
  );

  const setStep = useCallback((s: OnboardingStep) => {
    setStepState(s);
  }, []);

  const value = useMemo<OnboardingContextValue>(
    () => ({
      step,
      setStep,
      isAuthenticated,
      setIsAuthenticated,
      professionalForm,
      setProfessionalForm,
      sessionId,
      setSessionId,
      patient,
      setPatient,
      resolveStepFromProfile,
    }),
    [
      step,
      setStep,
      isAuthenticated,
      professionalForm,
      sessionId,
      patient,
      resolveStepFromProfile,
    ]
  );

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding() {
  const ctx = useContext(OnboardingContext);
  if (!ctx) {
    throw new Error("useOnboarding must be used within OnboardingProvider");
  }
  return ctx;
}

